import { generatePath, useNavigate } from 'react-router-dom';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { createForm, useCreateFormStore } from '@components/form';
import { MaxLengthCounter } from '@components/max-length-counter';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useCreateJourney } from 'api/journeys/hooks';
import { routePaths } from 'ui/authenticated/navigation';

const MAX_TITLE_LENGTH = 50;

type CreateJourneyFormData = {
  title: string;
};

type CreateJourneyModalProps = {
  workspaceId: string;
};

const Form = createForm<CreateJourneyFormData>({ space: 0 });

export const useCreateJourneyModal = () => useModal(CreateJourneyModal);

const CreateJourneyModal = ({ workspaceId }: CreateJourneyModalProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const navigate = useNavigate();
  const createMutation = useCreateJourney();

  const title = (formStore.useFieldValue('title') as string) ?? '';
  const trimmedTitle = title.trim();

  const handleSubmit = async () => {
    if (!trimmedTitle) {
      return;
    }

    const data = await createMutation.mutateAsync({
      title: trimmedTitle,
      workspace: workspaceId,
    });
    hideModal();
    navigate(
      generatePath(routePaths.editJourney, {
        workspaceId,
        journeyId: data.shortId,
        '*': '',
      })
    );
  };

  return (
    <Modal isLoading={createMutation.isLoading}>
      <Form store={formStore} onSubmit={handleSubmit}>
        <Modal.SimpleHeader title={t('title')} />
        <Modal.Content>
          <Stack space={1}>
            <Form.TextField
              id="title"
              label={t('journeyNameLabel')}
              placeholder={t('journeyNamePlaceholder')}
              autoFocus
              autoComplete="off"
              maxChars={MAX_TITLE_LENGTH}
            />
            <MaxLengthCounter currentLength={title.length} maxLength={MAX_TITLE_LENGTH} />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <CancelSaveButtons
            alwaysShow
            onCancel={hideModal}
            onSave={handleSubmit}
            translationsOverride={{ save: t('createJourney') }}
            isDisabled={createMutation.isLoading}
            isSaveDisabled={!trimmedTitle}
          />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Create journey',
    journeyNameLabel: 'Journey name',
    journeyNamePlaceholder: 'Enter journey name',
    createJourney: 'Create journey',
  },
};
